/**
 * Collections — dedicated full-page view of the signed-in user's collections.
 *
 * Wraps the same create/rename/delete primitives as ShelvesManager but presents
 * them as a generous, scannable grid of cards rather than a dense settings row.
 * Each card routes to the existing ShelfPage at /shelf/:owner/:slug.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, BookMarked, Plus, Trash2, Pencil, Check, X, Globe, Lock } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import LoginPrompt from "../components/LoginPrompt";

export default function Collections() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [shelves, setShelves] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");

  const load = async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/shelves/me");
      setShelves(Array.isArray(data) ? data : []);
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[collections] soft-fail:", e?.message); } finally { setFetching(false); }
  };

  useEffect(() => {
    if (loading || !user) return;
    load();
  }, [user, loading]);

  if (loading) return null;
  if (!user) {
    return (
      <LoginPrompt
        title="Your collections, waiting."
        blurb="Sign in to group saves into named shelves — reading lists, paths through traditions, your own correlations."
      />
    );
  }

  const create = async (e) => {
    e?.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post("/shelves", { name });
      setNewName("");
      await load();
      toast.success("Collection created.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create.");
    } finally { setCreating(false); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.name}"? Saves themselves stay — only this grouping is removed.`)) return;
    try {
      await api.delete(`/shelves/${s.id}`);
      setShelves((arr) => arr.filter((x) => x.id !== s.id));
      toast.success("Collection deleted.");
    } catch { toast.error("Could not delete."); }
  };

  const saveRename = async (s) => {
    const name = editText.trim();
    if (!name) { setEditingId(null); return; }
    try {
      await api.patch(`/shelves/${s.id}`, { name });
      setShelves((arr) => arr.map((x) => (x.id === s.id ? { ...x, name } : x)));
      setEditingId(null);
      toast.success("Renamed.");
    } catch { toast.error("Could not rename."); }
  };

  const toggleVisibility = async (s) => {
    const next = s.visibility === "public" ? "private" : "public";
    try {
      await api.patch(`/shelves/${s.id}`, { name: s.name, visibility: next });
      setShelves((arr) => arr.map((x) => (x.id === s.id ? { ...x, visibility: next } : x)));
      toast.success(next === "public" ? "Collection is now public." : "Collection is now private.");
    } catch { toast.error("Could not update."); }
  };

  // Owner identifier for ShelfPage — `user_id` always works (handle may be empty
  // until the user claims one). ShelfPage already uses owner just to disambiguate
  // the slug lookup; the backend gates owner-viewing on session ownership, not URL.
  const ownerKey = user.handle || user.user_id;

  return (
    <div data-testid="collections-page" className="min-h-[calc(100vh-80px)]">
      <div className="max-w-4xl mx-auto px-6 sm:px-10 py-16 sm:py-24 pb-32">
        <header className="mb-12">
          <button
            onClick={() => navigate("/settings#you")}
            data-testid="collections-back"
            className="reaction-pill mb-8 inline-flex"
          >
            <ArrowLeft size={12} /> Back to You
          </button>
          <div className="label mb-3 flex items-center gap-2">
            <BookMarked size={11} /> Collections
          </div>
          <h1 className="serif text-4xl sm:text-5xl leading-tight">Your collections.</h1>
          <p className="serif italic text-lg text-muted-foreground mt-5 max-w-2xl">
            Group your saves into named shelves — &quot;Reading list 2026,&quot; &quot;My path through Vedanta.&quot;
            Public collections appear on your profile.
          </p>
        </header>

        <form onSubmit={create} className="flex items-center gap-2 mb-10">
          <input
            data-testid="collection-new-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New collection name…"
            maxLength={80}
            className="input-line flex-1 text-sm"
          />
          <button
            data-testid="collection-create"
            type="submit"
            disabled={creating || !newName.trim()}
            className="btn-gold disabled:opacity-40 inline-flex items-center gap-1"
          >
            <Plus size={12} /> Add
          </button>
        </form>

        {fetching && (
          <p className="serif italic text-muted-foreground py-8 text-center">Loading…</p>
        )}

        {!fetching && shelves.length === 0 && (
          <p className="serif italic text-muted-foreground py-8 text-center">
            You don&apos;t have any collections yet. Create your first above.
          </p>
        )}

        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-border">
          {shelves.map((s) => {
            const isEditing = editingId === s.id;
            const linkTo = `/shelf/${ownerKey}/${s.slug}`;
            const count = s.item_count ?? s.pin_count ?? 0;
            return (
              <li
                key={s.id}
                data-testid={`collection-card-${s.id}`}
                className="bg-card p-6 group"
              >
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <input
                      autoFocus
                      data-testid={`collection-rename-input-${s.id}`}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") saveRename(s); if (e.key === "Escape") setEditingId(null); }}
                      className="input-line flex-1 text-sm"
                      maxLength={80}
                    />
                    <button onClick={() => saveRename(s)} className="text-[hsl(var(--primary))]" title="Save">
                      <Check size={14} />
                    </button>
                    <button onClick={() => setEditingId(null)} className="text-muted-foreground" title="Cancel">
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Link
                      to={linkTo}
                      data-testid={`collection-link-${s.id}`}
                      className="block"
                    >
                      <h3 className="serif text-2xl text-foreground group-hover:text-[hsl(var(--primary))] transition-colors leading-tight">
                        {s.name}
                      </h3>
                      <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-3 flex items-center gap-2">
                        <span>{count} item{count !== 1 ? "s" : ""}</span>
                      </div>
                    </Link>
                    <div className="mt-4 flex items-center gap-3 flex-wrap">
                      <button
                        data-testid={`collection-toggle-vis-${s.id}`}
                        onClick={() => toggleVisibility(s)}
                        className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                      >
                        {s.visibility === "public" ? <><Globe size={11} /> public</> : <><Lock size={11} /> private</>}
                      </button>
                      <button
                        data-testid={`collection-rename-${s.id}`}
                        onClick={() => { setEditingId(s.id); setEditText(s.name); }}
                        className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                      >
                        <Pencil size={11} /> rename
                      </button>
                      <button
                        data-testid={`collection-delete-${s.id}`}
                        onClick={() => remove(s)}
                        className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--destructive,0_70%_45%))] inline-flex items-center gap-1 ml-auto"
                      >
                        <Trash2 size={11} /> delete
                      </button>
                    </div>
                  </>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
